import type { ColumnSort, SortingState } from '@tanstack/react-table';
import { atom } from 'jotai';
import { atomWithLocation } from 'jotai-location';
import { z } from 'zod';
import { applyLocation, SearchParam } from './searchParams';

export const sortDescSchema = z.boolean().catch(false);
export const sortFieldSchema = z.string().nullable();

export const createSortingAtom = ({
  defaultSorting,
}: {
  defaultSorting: ColumnSort;
}) => {
  const paramsAtom = atomWithLocation({
    applyLocation,
    getLocation: () => new URLSearchParams(window.location.search),
  });
  return atom<SortingState, [SortingState], SortingState>(
    (get) => {
      const searchParams = get(paramsAtom);
      const desc = sortDescSchema.parse(searchParams?.has(SearchParam.desc));
      const id = sortFieldSchema.parse(
        searchParams?.get(SearchParam.sortField),
      );
      const defaultDesc = sortDescSchema.parse(defaultSorting.desc);
      const defaultId = sortFieldSchema.parse(defaultSorting.id)!;
      return id ? [{ desc, id }] : [{ desc: defaultDesc, id: defaultId }];
    },
    (get, set, sortingState) => {
      const tableSort = sortingState[0];
      set(paramsAtom, (searchParams) => {
        const params = new URLSearchParams(searchParams);
        const current = get(paramsAtom);

        if (tableSort) {
          const sortDesc = sortDescSchema.parse(tableSort.desc);
          const sortField = sortFieldSchema.parse(tableSort.id)!;

          if (
            sortField === defaultSorting.id &&
            sortDesc === defaultSorting.desc
          ) {
            params.delete(SearchParam.desc);
            params.delete(SearchParam.sortField);
          } else {
            if (sortDesc) {
              params.set(SearchParam.desc, '');
            } else {
              params.delete(SearchParam.desc);
            }
            params.set(SearchParam.sortField, sortField);
          }
        } else {
          if (current.get(SearchParam.sortField)) {
            params.set(SearchParam.desc, '');
          } else {
            params.set(SearchParam.sortField, defaultSorting.id);
            if (defaultSorting.desc) {
              params.delete(SearchParam.desc);
            } else {
              params.set(SearchParam.desc, '');
            }
          }
        }

        params.delete(SearchParam.pageIndex);

        return params;
      });

      return sortingState;
    },
  );
};
